/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { Collapsible } from 'src/core/components/Collapsible';
import { FilePicker } from 'src/core/components/FilePicker';
import { LuisText, LuisTextProps } from 'src/core/components/LuisText';
import { useFormState } from 'src/core/hooks/useFormState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylist } from 'src/core/themes/luisStylist';
import { FormValidators } from 'src/core/utils/formValidators';

export type ImportSublistModalDto = {
    /**
     * The file that will be imported.
     */
    sublistFile: File;
};

const messages = defineMessages({
    modalTitle: {
        id: 'entities.importSublistModal.modalTitle',
        defaultMessage: 'Import sublists to list entity'
    },
    filePickerLabel: {
        id: 'entities.importSublistModal.filePickerLabel',
        defaultMessage: 'Choose sublist file (JSON format) ...'
    },
    sampleLink: {
        id: 'entities.importSublistModal.sampleLink',
        defaultMessage: 'Learn more about the supported file format'
    },
    sampleTitle: {
        id: 'entities.importSublistModal.sampleTitle',
        defaultMessage: 'Lists file syntax'
    },
    sampleBody: {
        id: 'entities.importSublistModal.sampleBody',
        defaultMessage: 'The file should contain an array of sublists that follow the following JSON format:'
    }
});

const { styleComponent } = getStylist('ImportSublistsModal');
const SampleTitle = styleComponent<LuisTextProps>(LuisText)('SampleHelpTitle', theme => ({ marginBottom: theme.titleMargin }));

const sampleJSON = [
    { canonicalForm: 'Alexandria', list: ['Pearl of the Mediterranean'] },
    { canonicalForm: 'Cairo', list: ['The City of a Thousand Minarets'] }
];

export const ImportSublistsModal = (props: BasicModalProps<ImportSublistModalDto>) => {
    const { onSubmit, onDismiss } = props;

    const [collapse, setCollapse] = React.useState(true);
    const { formState, updateFormState, formStateDto } = useFormState<ImportSublistModalDto>({
        sublistFile: [
            new Blob([], { type: 'file' }) as File,
            FormValidators.createRequiredValidator(),
            FormValidators.createExtensionValidator('json')
        ]
    });
    const { localize } = useLocalization();

    return (
        <BasicModal
            {...props}
            onDismiss={onDismiss}
            valid={formState.form.valid}
            onSubmit={() => onSubmit(formStateDto)}
            dialogContentProps={{ title: localize(messages.modalTitle) }}>
            <Stack tokens={{ childrenGap: 16 }}>
                <FilePicker
                    required
                    accept=".json"
                    label={localize(messages.filePickerLabel)}
                    errorMessage={formState.fields.sublistFile.errorMessage}
                    onFilesPicked={files => updateFormState({ sublistFile: files.item(0) })}
                />

                <Collapsible title={localize(messages.sampleLink)} collapsed={collapse} onTitleClick={() => setCollapse(!collapse)}>
                    <SampleTitle variant="large" block as="h3">
                        {localize(messages.sampleTitle)}
                    </SampleTitle>

                    <LuisText>{localize(messages.sampleBody)}</LuisText>

                    <code>
                        <pre>{JSON.stringify(sampleJSON, null, 4)}</pre>
                    </code>
                </Collapsible>
            </Stack>
        </BasicModal>
    );
};
